import { useState } from 'react';
import { FileText, Download, Plus, Trash2, User, Building, MapPin, ChevronDown, QrCode } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';

export default function AdminProspecting() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('single');
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    address: '',
    postal_code: '',
    city: '',
    sector: '',
    company_mention: 'company_name',
    intro_custom: "c'est une belle aventure qui commence",
    website_status: 'pas_de_site',
    website_status_custom: '',
    closing_choice: 'votre_activite',
    sender_city: 'Bourg-en-Bresse'
  });
  
  // Batch mode
  const [recipients, setRecipients] = useState([
    { first_name: '', last_name: '', company_name: '', address: '', postal_code: '', city: '', sector: '' }
  ]);
  const [batchSettings, setBatchSettings] = useState({
    company_mention: 'company_name',
    intro_choice: 'belle_aventure',
    website_status: 'pas_de_site',
    website_status_custom: '',
    closing_choice: 'votre_activite',
    sender_city: 'Bourg-en-Bresse'
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleRecipientChange = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const addRecipient = () => {
    setRecipients([
      ...recipients,
      { first_name: '', last_name: '', company_name: '', address: '', postal_code: '', city: '', sector: '' }
    ]);
  };

  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const generateSingleLetter = async () => {
    if (!formData.first_name || !formData.last_name) {
      alert('Veuillez remplir au moins le prénom et le nom');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/prospecting/letter`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lettre_${formData.first_name}_${formData.last_name}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert('Erreur lors de la génération');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  const generateBatchLetters = async () => {
    const validRecipients = recipients.filter(r => r.first_name && r.last_name);
    if (validRecipients.length === 0) {
      alert('Veuillez ajouter au moins un destinataire avec prénom et nom');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/prospecting/batch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipients: validRecipients,
          ...batchSettings
        })
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lettres_prospection_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        alert('Erreur lors de la génération');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la génération');
    } finally {
      setLoading(false);
    }
  };

  // Dropdown component
  const Select = ({ name, value, onChange, options, className = '' }) => (
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full h-10 px-3 pr-8 rounded-md border border-input bg-background appearance-none cursor-pointer ${className}`}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
    </div>
  );

  // Get dynamic text for preview
  const getCompanyText = () => {
    if (formData.company_mention === 'company_name' && formData.company_name) {
      return formData.company_name;
    }
    return 'votre entreprise';
  };

  const getIntroText = () => {
    return formData.intro_custom || "c'est une belle aventure qui commence";
  };

  const getWebsiteText = () => {
    if (formData.website_status === 'pas_de_site') return "vous n'aviez pas encore de site internet";
    if (formData.website_status === 'instagram') return "vous utilisiez votre page Instagram comme vitrine";
    if (formData.website_status === 'site_ancien') return "votre site actuel date un peu et mériterait un rafraîchissement";
    if (formData.website_status === 'custom' && formData.website_status_custom) return formData.website_status_custom;
    return "vous n'aviez pas encore de site internet";
  };

  const getClosingText = () => {
    if (formData.closing_choice === 'votre_activite') return "votre activité";
    if (formData.closing_choice === 'domaine_secteur' && formData.sector) return `le domaine de ${formData.sector}`;
    if (formData.closing_choice === 'projet_ville' && formData.city) return `votre projet à ${formData.city}`;
    return "votre activité";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Générateur de Courriers</h1>
          <p className="text-muted-foreground">Prospection BODACC - Lettres personnalisées</p>
        </div>
        
        <div className="flex gap-2 p-1 bg-muted rounded-lg">
          <button
            onClick={() => setMode('single')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'single' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Lettre unique
          </button>
          <button
            onClick={() => setMode('batch')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === 'batch' ? 'bg-background shadow text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Lot de lettres
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form - 3 columns */}
        <div className="lg:col-span-3 space-y-4">
          {mode === 'single' ? (
            <>
              {/* Recipient Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <User className="h-4 w-4" />
                    Destinataire
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Prénom *</label>
                      <Input name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Jean" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Nom *</label>
                      <Input name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Dupont" />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Entreprise</label>
                      <Input name="company_name" value={formData.company_name} onChange={handleChange} placeholder="SARL Dupont" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">Secteur d'activité</label>
                      <Input name="sector" value={formData.sector} onChange={handleChange} placeholder="la restauration" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Adresse</label>
                    <Input name="address" value={formData.address} onChange={handleChange} placeholder="123 rue de la République" />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">CP</label>
                      <Input name="postal_code" value={formData.postal_code} onChange={handleChange} placeholder="01000" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs font-medium text-muted-foreground">Ville</label>
                      <Input name="city" value={formData.city} onChange={handleChange} placeholder="Bourg-en-Bresse" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Choices */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileText className="h-4 w-4" />
                    Personnalisation du texte
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Company mention */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      "Félicitations pour le lancement de..."
                    </label>
                    <Select
                      name="company_mention"
                      value={formData.company_mention}
                      onChange={handleChange}
                      options={[
                        { value: 'company_name', label: formData.company_name || '[Nom de l\'entreprise]' },
                        { value: 'votre_entreprise', label: 'votre entreprise' }
                      ]}
                    />
                  </div>

                  {/* Intro choice - custom text */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Suite de l'intro (après "Félicitations...")
                    </label>
                    <Input
                      name="intro_custom"
                      value={formData.intro_custom}
                      onChange={handleChange}
                      placeholder="c'est une belle aventure qui commence"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Exemples : "c'est une belle aventure qui commence" / "se lancer dans [secteur], ça demande du courage"
                    </p>
                  </div>

                  {/* Website status */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      "En cherchant à vous contacter, j'ai remarqué que..."
                    </label>
                    <Select
                      name="website_status"
                      value={formData.website_status}
                      onChange={handleChange}
                      options={[
                        { value: 'pas_de_site', label: "Vous n'aviez pas encore de site internet" },
                        { value: 'instagram', label: "Vous utilisiez votre page Instagram comme vitrine" },
                        { value: 'site_ancien', label: "Votre site actuel date un peu et mériterait un rafraîchissement" },
                        { value: 'custom', label: "Autre (personnalisé)..." }
                      ]}
                    />
                    {formData.website_status === 'custom' && (
                      <Input
                        name="website_status_custom"
                        value={formData.website_status_custom}
                        onChange={handleChange}
                        placeholder="votre présence en ligne était limitée..."
                        className="mt-2"
                      />
                    )}
                  </div>

                  {/* Closing choice */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      "...et plein de réussite dans..."
                    </label>
                    <Select
                      name="closing_choice"
                      value={formData.closing_choice}
                      onChange={handleChange}
                      options={[
                        { value: 'votre_activite', label: "votre activité" },
                        { value: 'domaine_secteur', label: `le domaine de ${formData.sector || '[secteur]'}` },
                        { value: 'projet_ville', label: `votre projet à ${formData.city || '[ville]'}` }
                      ]}
                    />
                  </div>

                  {/* Sender city */}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      "Fait à..."
                    </label>
                    <Input
                      name="sender_city"
                      value={formData.sender_city}
                      onChange={handleChange}
                      placeholder="Bourg-en-Bresse"
                    />
                  </div>
                </CardContent>
              </Card>

              <Button onClick={generateSingleLetter} disabled={loading} className="w-full btn-primary py-5">
                <Download className="mr-2 h-5 w-5" />
                {loading ? 'Génération...' : 'Télécharger le PDF'}
              </Button>
            </>
          ) : (
            /* BATCH MODE */
            <>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-base">
                      <Building className="h-4 w-4" />
                      Destinataires ({recipients.length})
                    </span>
                    <Button variant="outline" size="sm" onClick={addRecipient}>
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 max-h-96 overflow-y-auto">
                  {recipients.map((r, i) => (
                    <div key={i} className="p-3 border border-border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-muted-foreground">#{i + 1}</span>
                        {recipients.length > 1 && (
                          <button onClick={() => removeRecipient(i)} className="text-destructive hover:text-destructive/80">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={r.first_name} onChange={(e) => handleRecipientChange(i, 'first_name', e.target.value)} placeholder="Prénom *" />
                        <Input value={r.last_name} onChange={(e) => handleRecipientChange(i, 'last_name', e.target.value)} placeholder="Nom *" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={r.company_name} onChange={(e) => handleRecipientChange(i, 'company_name', e.target.value)} placeholder="Entreprise" />
                        <Input value={r.city} onChange={(e) => handleRecipientChange(i, 'city', e.target.value)} placeholder="Ville" />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Options communes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Select
                    name="website_status"
                    value={batchSettings.website_status}
                    onChange={(e) => setBatchSettings({...batchSettings, website_status: e.target.value})}
                    options={[
                      { value: 'pas_de_site', label: "Pas encore de site internet" },
                      { value: 'instagram', label: "Instagram comme vitrine" },
                      { value: 'site_ancien', label: "Site qui date un peu" }
                    ]}
                  />
                </CardContent>
              </Card>

              <Button onClick={generateBatchLetters} disabled={loading} className="w-full btn-primary py-5">
                <Download className="mr-2 h-5 w-5" />
                {loading ? 'Génération...' : `Générer ${recipients.filter(r => r.first_name && r.last_name).length} lettre(s)`}
              </Button>
            </>
          )}
        </div>

        {/* Preview - 2 columns */}
        <div className="lg:col-span-2">
          <Card className="sticky top-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Aperçu</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white border rounded-lg p-3 text-[9px] space-y-2 shadow-inner max-h-[70vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-start">
                  <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-purple-600 rounded" />
                </div>
                
                {/* Recipient */}
                <div className="text-right text-gray-800 pt-2">
                  <p className="font-bold">{formData.first_name || 'Prénom'} {formData.last_name || 'Nom'}</p>
                  {formData.company_name && <p>{formData.company_name}</p>}
                  {formData.address && <p>{formData.address}</p>}
                  {(formData.postal_code || formData.city) && <p>{formData.postal_code} {formData.city}</p>}
                </div>
                
                {/* Date - just before greeting */}
                <div className="text-right text-gray-500 text-[8px]">
                  Fait à {formData.sender_city}, le {new Date().toLocaleDateString('fr-FR')}
                </div>
                
                {/* Greeting */}
                <p className="font-bold pt-1">Bonjour {formData.first_name || 'Prénom'},</p>
                
                {/* Para 1 */}
                <p className="text-gray-600 leading-tight">
                  Félicitations pour le lancement de {getCompanyText()} ! {getIntroText().charAt(0).toUpperCase() + getIntroText().slice(1)}. Je vous souhaite vraiment le meilleur pour la suite.
                </p>
                
                {/* Para 2 */}
                <p className="text-gray-600 leading-tight">
                  En cherchant à vous contacter, j'ai remarqué que {getWebsiteText()}. C'est souvent la première chose que vos futurs clients vont chercher...
                </p>
                
                {/* Para 3 - shortened */}
                <p className="text-gray-500 leading-tight italic">
                  [Présentation personnelle de Valentin...]
                </p>
                
                {/* Para 4 - shortened */}
                <p className="text-gray-500 leading-tight italic">
                  [Ce qui me différencie...]
                </p>
                
                {/* Para 5 - new text */}
                <p className="text-gray-600 leading-tight">
                  C'est pourquoi j'ai créé quelque chose spécialement pour les gens qui se lancent, je vous laisse découvrir ça en page suivante.
                </p>
                
                {/* QR section */}
                <div className="flex items-center gap-2 py-1">
                  <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                    <QrCode className="h-5 w-5 text-gray-800" />
                  </div>
                  <div>
                    <p className="font-medium text-[8px]">Découvrez mon travail en 30 secondes</p>
                    <p className="text-purple-600 text-[8px]">axeris-labs.fr</p>
                  </div>
                </div>
                
                {/* Closing - AFTER QR */}
                <p className="text-gray-600 leading-tight">
                  Et si ce n'est pas le bon moment, pas de souci. Je vous souhaite une très belle aventure avec {formData.company_name || 'votre entreprise'}, et plein de réussite dans {getClosingText()} !
                </p>
                
                {/* Signature */}
                <div className="pt-2">
                  <p className="text-gray-600">Bien cordialement,</p>
                  <p className="font-bold mt-2">Valentin Bechameil</p>
                  <p className="text-gray-500">Axeris Labs</p>
                </div>
                
                {/* Page 2 indicator */}
                <div className="border-t border-dashed border-gray-300 pt-2 mt-2">
                  <p className="text-center text-gray-400 text-[8px]">--- Page 2 ---</p>
                </div>
                
                {/* Offer box */}
                <div className="bg-purple-50 border border-purple-300 rounded p-2">
                  <p className="font-bold text-purple-600 text-[10px]">🌱 Offre Bâtisseurs</p>
                  <p className="text-gray-500 text-[8px]">Conçu pour ceux qui osent se lancer.</p>
                  <p className="text-purple-700 font-bold mt-1">999€ <span className="font-normal text-gray-500">maximum</span></p>
                  <p className="text-gray-600 text-[8px] leading-tight mt-1">
                    <b>Pour qui ?</b> Étudiants, Jeunes entrepreneurs, Entreprises locales<br/>
                    <b>Ma philosophie :</b> Pas de limites artificielles, un vrai échange humain.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
